import { Menu, MenuItem, Popover, useMediaQuery } from '@paintscout/ui'
import PhoneIcon from '@material-ui/icons/PhoneOutlined'
import MailIcon from '@material-ui/icons/MailOutline'
import EditIcon from '@material-ui/icons/EditOutlined'
import PeopleIcon from '@material-ui/icons/PeopleAltOutlined'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import React, { useState } from 'react'
import { openMailto, openTel } from '../../util/deepLinks'
import QuoteNextDateEditor from './QuoteNextDateEditor'
import QuoteDocumentHeaderDesktop from './QuoteDocumentHeaderDesktop'
import QuoteDocumentHeaderMobile from './QuoteDocumentHeaderMobile'
import QuoteDocumentHeaderMobileSheet from './QuoteDocumentHeaderMobileSheet'
import type { QuoteDocumentHeaderSection } from './QuoteDocumentHeaderMobileSheet'
import { useQuoteDocumentHeaderStyles } from './quoteDocumentHeaderStyles'
import { useQuoteDocumentHeaderData } from './useQuoteDocumentHeaderData'

export interface QuoteDocumentHeaderProps {
  /** Render without the header's own card border/rounding/gap — for embedding in a shared card. */
  flush?: boolean
  /**
   * `estimate` (default): doc label = Estimate/Invoice, a clickable Issued/Expires date chip, and the
   * three contact columns below. `work-order`: doc label = Work Order, a single plain document date,
   * and no columns (that info lives in the Work Order summary instead).
   */
  variant?: 'estimate' | 'work-order'
}

/**
 * The redesigned quote-document header for the new estimate editor (estimateNext). Owns the data (via
 * {@link useQuoteDocumentHeaderData}) and picks the layout: the full card on desktop
 * ({@link QuoteDocumentHeaderDesktop}), the collapsible one on phones
 * ({@link QuoteDocumentHeaderMobile}).
 *
 * The two pop-ups both layouts open live here rather than in either layout, because a breakpoint change
 * mid-session swaps the layout and an open menu has to survive it:
 *   - the "Prepared for" contact menu (Edit / Swap or Create / Add Secondary + tel/mailto), mirroring
 *     the classic ContactCard menu
 *   - the "Secondary Contact" menu (Edit / Swap or Create / Remove + tel/mailto), mirroring the classic
 *     SecondaryContactCard menu
 *   - the date/expiry editor (the classic QuoteDateCard: issued-date picker, expiry picker, and the
 *     enable/disable-expiry toggle), which only an estimate has
 * Both portal out of the card, so where they sit in this tree doesn't affect layout.
 */
export default function QuoteDocumentHeader({ flush, variant = 'estimate' }: QuoteDocumentHeaderProps = {}) {
  const classes = useQuoteDocumentHeaderStyles()
  const isMobile = useMediaQuery('xs')
  const data = useQuoteDocumentHeaderData({ variant })
  // Which section's detail sheet the phone layout has open. Local rather than in the data hook: the
  // sheet is a phone-only surface, so a breakpoint change that swaps in the desktop layout should take
  // it down with it — which unmounting on `isMobile` does.
  const [sheetSection, setSheetSection] = useState<QuoteDocumentHeaderSection | null>(null)
  const {
    onEditContact,
    onSelectContact,
    onSelectSecondaryContact,
    onEditSecondaryContact,
    onRemoveSecondaryContact,
    isWorkOrder,
    contact,
    contactPhones,
    contactEmails,
    canCreateContact,
    canAddSecondaryContact,
    contactAnchorEl,
    setContactAnchorEl,
    secondaryContactPhones,
    secondaryContactEmails,
    secondaryContactAnchorEl,
    setSecondaryContactAnchorEl,
    dateAnchorEl,
    setDateAnchorEl
  } = data

  return (
    <>
      {isMobile ? (
        <>
          <QuoteDocumentHeaderMobile flush={flush} data={data} onOpenSection={setSheetSection} />
          <QuoteDocumentHeaderMobileSheet section={sheetSection} onClose={() => setSheetSection(null)} data={data} />
        </>
      ) : (
        <QuoteDocumentHeaderDesktop flush={flush} data={data} />
      )}

      <Menu anchorEl={contactAnchorEl} open={Boolean(contactAnchorEl)} onClose={() => setContactAnchorEl(null)}>
        {!!contact && !!onEditContact && (
          <MenuItem
            icon={EditIcon}
            onClick={() => {
              setContactAnchorEl(null)
              onEditContact()
            }}
          >
            Edit Contact
          </MenuItem>
        )}
        {!!onSelectContact && (
          <MenuItem
            icon={PeopleIcon}
            onClick={() => {
              setContactAnchorEl(null)
              onSelectContact()
            }}
          >
            {`Swap${canCreateContact ? ' or Create' : ''} Contact`}
          </MenuItem>
        )}
        {canAddSecondaryContact && <MenuItem disabled classes={{ root: classes.menuItemDivider }} />}
        {canAddSecondaryContact && (
          <MenuItem
            icon={AddIcon}
            onClick={() => {
              setContactAnchorEl(null)
              onSelectSecondaryContact()
            }}
          >
            Add Secondary Contact
          </MenuItem>
        )}
        {(contactPhones.length > 0 || contactEmails.length > 0) && (
          <MenuItem disabled classes={{ root: classes.menuItemDivider }} />
        )}
        {contactPhones.map((phone) => (
          <MenuItem
            key={phone}
            icon={PhoneIcon}
            onClick={() => {
              setContactAnchorEl(null)
              openTel(phone)
            }}
          >
            {phone}
          </MenuItem>
        ))}
        {contactEmails.map((email) => (
          <MenuItem
            key={email}
            icon={MailIcon}
            onClick={() => {
              setContactAnchorEl(null)
              openMailto(email)
            }}
          >
            {email}
          </MenuItem>
        ))}
      </Menu>

      {/* The secondary contact's own menu — Edit / Swap or Create / Remove, then its deep-links.
          Mirrors the classic SecondaryContactCard menu (QuoteHeaderFull.tsx:89). */}
      <Menu
        anchorEl={secondaryContactAnchorEl}
        open={Boolean(secondaryContactAnchorEl)}
        onClose={() => setSecondaryContactAnchorEl(null)}
      >
        {!!onEditSecondaryContact && (
          <MenuItem
            icon={EditIcon}
            onClick={() => {
              setSecondaryContactAnchorEl(null)
              onEditSecondaryContact()
            }}
          >
            Edit Secondary Contact
          </MenuItem>
        )}
        {!!onSelectSecondaryContact && (
          <MenuItem
            icon={PeopleIcon}
            onClick={() => {
              setSecondaryContactAnchorEl(null)
              onSelectSecondaryContact()
            }}
          >
            {`Swap${canCreateContact ? ' or Create' : ''} Contact`}
          </MenuItem>
        )}
        {!!onRemoveSecondaryContact && <MenuItem disabled classes={{ root: classes.menuItemDivider }} />}
        {!!onRemoveSecondaryContact && (
          <MenuItem
            icon={RemoveIcon}
            onClick={() => {
              setSecondaryContactAnchorEl(null)
              onRemoveSecondaryContact()
            }}
          >
            Remove Secondary Contact
          </MenuItem>
        )}
        {(secondaryContactPhones.length > 0 || secondaryContactEmails.length > 0) && (
          <MenuItem disabled classes={{ root: classes.menuItemDivider }} />
        )}
        {secondaryContactPhones.map((phone) => (
          <MenuItem
            key={phone}
            icon={PhoneIcon}
            onClick={() => {
              setSecondaryContactAnchorEl(null)
              openTel(phone)
            }}
          >
            {phone}
          </MenuItem>
        ))}
        {secondaryContactEmails.map((email) => (
          <MenuItem
            key={email}
            icon={MailIcon}
            onClick={() => {
              setSecondaryContactAnchorEl(null)
              openMailto(email)
            }}
          >
            {email}
          </MenuItem>
        ))}
      </Menu>

      {!isWorkOrder && (
        <Popover
          open={Boolean(dateAnchorEl)}
          anchorEl={dateAnchorEl}
          onClose={() => setDateAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableEnforceFocus
          disableRestoreFocus
        >
          <div className={classes.datePopover}>
            <QuoteNextDateEditor />
          </div>
        </Popover>
      )}
    </>
  )
}
